import { useMutation, useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { cloneRecord, deleteRecord, fetchRecords } from '../services/ProductsService';
import { useEffect, useState } from 'react';
import { notifySuccess, notifyError } from '../../../components/ServiceNotifications';

// Module-scoped global filter state (shared across components)
let globalFilters: Record<string, string> = { status: 'Active' };
const filterListeners: Set<(filters: Record<string, string>) => void> = new Set();

const notifyFilterChange = (filters: Record<string, string>) => {
    globalFilters = filters;
    filterListeners.forEach(listener => listener(filters));
};

export const useProductsFilter = () => {
    const [filters, setFilters] = useState<Record<string, string>>(globalFilters);

    useEffect(() => {
        const listener = (newFilters: Record<string, string>) => setFilters(newFilters);
        filterListeners.add(listener);
        return () => {
            filterListeners.delete(listener);
        };
    }, []);

    const handleSetFilters = (newFilters: Record<string, string>) => {
        notifyFilterChange(newFilters);
    };

    const handleClearFilters = () => {
        notifyFilterChange({});
    };

    return { filters, handleSetFilters, handleClearFilters };
};

export const useProducts = () => {
    const queryClient = useQueryClient();
    const { filters } = useProductsFilter();
    const [keyword, setKeyword] = useState('');
    const [selection, setSelection] = useState<Record<string, boolean>>({});

    const limit = 10;
    const sort = { column: 'created', direction: 'desc' as const };

    const productsQuery = useInfiniteQuery({
        queryKey: ['products', keyword, filters, limit, sort.column, sort.direction],
        queryFn: ({ pageParam }: { pageParam: string | null }) =>
            fetchRecords({ limit, sort, nextToken: pageParam, filter: filters }),
        getNextPageParam: (lastPage) => {
            if (!lastPage || !lastPage.nextToken) return undefined;
            return lastPage.nextToken;
        },
        initialPageParam: null as string | null
    });

    const deleteMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => deleteRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Product deleted');
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['all-products'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const cloneMutation = useMutation({
        mutationFn: async (ids: string[]) => {
            const promises = ids.map(id => cloneRecord(id));
            return Promise.all(promises);
        },
        onSuccess: () => {
            notifySuccess('Product cloned');
            queryClient.invalidateQueries({ queryKey: ['products'] });
            queryClient.invalidateQueries({ queryKey: ['all-products'] });
        },
        onError: (error) => {
            notifyError(error);
        }
    });

    const fetchRecordsFunc = () => {
        queryClient.invalidateQueries({ queryKey: ['products'] });
    };

    const fetchMoreRecords = () => {
        if (productsQuery.hasNextPage) {
            productsQuery.fetchNextPage();
        }
    };

    const deleteRecords = (ids?: string[]) => {
        const selectedIds = ids || Object.keys(selection);
        if (selectedIds.length > 0) {
            deleteMutation.mutate(selectedIds);
        }
    };

    const cloneRecords = () => {
        const selectedIds = Object.keys(selection);
        if (selectedIds.length > 0) {
            cloneMutation.mutate(selectedIds);
        }
    };

    const handleFilterByKeyword = (newKeyword: string) => {
        setKeyword(newKeyword);
    };

    const allItems = productsQuery.data?.pages?.flatMap(page => (page && 'items' in page) ? page.items : []) || [];

    return {
        items: allItems,
        visibleItems: allItems,
        loading: productsQuery.isLoading,
        keyword,
        visibleCount: allItems.length,
        totalCount: allItems.length,
        nextToken: productsQuery.hasNextPage ? 'has-more' : null,
        fetchRecords: fetchRecordsFunc,
        fetchMoreRecords,
        deleteRecords,
        cloneRecords,
        handleFilterByKeyword,
        deleteMutation,
        cloneMutation,
        selection,
        setSelection
    };
};
